import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';

/**
 * Monthly share sparkline.
 *
 * Direction matters more than level: "19% and climbing" is a different conversation from
 * "19% and flat". Months with too little data to bound a counter span carry share_pct
 * null and are drawn as a gap, never as zero.
 */
const ShareSparkline = ({ points = [], width = 90, height = 24 }) => {
  const usable = points.filter((p) => p.share_pct != null);

  if (usable.length < 2) {
    return (
      <Typography variant="caption" color="text.secondary">
        —
      </Typography>
    );
  }

  const values = usable.map((p) => p.share_pct);
  const max = Math.max(...values);
  const min = Math.min(...values);
  const span = max - min || 1;
  const stepX = width / (points.length - 1 || 1);

  // Null months break the line rather than dragging it to zero.
  const segments = [];
  let current = [];
  points.forEach((p, i) => {
    if (p.share_pct == null) {
      if (current.length) segments.push(current);
      current = [];
      return;
    }
    const x = i * stepX;
    const y = height - ((p.share_pct - min) / span) * (height - 4) - 2;
    current.push(`${x.toFixed(1)},${y.toFixed(1)}`);
  });
  if (current.length) segments.push(current);

  const first = values[0];
  const last = values[values.length - 1];
  const rising = last >= first;
  const stroke = rising ? '#2e7d32' : '#ed6c02';

  const title = points
    .map((p) => `${p.month}: ${p.share_pct == null ? 'n/a' : `${p.share_pct}%`}`)
    .join('  ·  ');

  return (
    <Tooltip title={title} arrow>
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
        <svg width={width} height={height} style={{ display: 'block' }}>
          {segments.map((seg, i) => (
            <polyline
              key={i}
              points={seg.join(' ')}
              fill="none"
              stroke={stroke}
              strokeWidth="1.5"
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          ))}
        </svg>
        <Typography variant="caption" sx={{ color: stroke, fontWeight: 600 }}>
          {rising ? '▲' : '▼'}
        </Typography>
      </Box>
    </Tooltip>
  );
};

export default ShareSparkline;
